import { action, computed, makeObservable, observable } from 'mobx';
import { decodeHTML } from '../helpers/html-helper';
import { MainStore } from './MainStore';
import { IArticleSetItem, IMixedMediaOfBrick } from './types';

export class MixedMediaStore {
    public mainStore: MainStore;


    // @observable:
    // UI
    public displayMediaOverlay: boolean = false;
    public mediaOverlayImages: { mainImgIndex: number; imgPath: string }[] = [];
    public mixedMediaBricks: IMixedMediaOfBrick[] = [];
    public detailsModalOpen: boolean = false;
    public masterArticleNo: number | null = null;
    public brickNo: number | null = null;
    public altTagForFlexibleSetImage: string = '';
    private mediaOverlayImageIndex: number = 1;

    constructor(mainStore: MainStore) {
        this.mainStore = mainStore;

        this.collectMediaOverlayImages();

        this.mixedMediaBricks = mainStore.dataStore.mixedMedia.sortedMixedMediaForBricks;
        this.altTagForFlexibleSetImage = mainStore.dataStore.mixedMedia.altTagForFlexibleSetImage;

        makeObservable(this, {
            displayMediaOverlay: observable,
            mediaOverlayImages: observable,
            mixedMediaBricks: observable,
            detailsModalOpen: observable,
            getMixedMediaImages: computed,
            getSetImage: computed,
            getImagesForCarousel: computed,
            updateMediaOverlayVisibility: action,
            setDetailsModalOpen: action,
            getMediaOverlayImageIndex: computed,
            getImageCount: computed,
            collectMediaOverlayImages: action
        });
    }

    collectMediaOverlayImages(): void {
        let mainImgIndex = 1;
        this.mediaOverlayImages = []; // clean up before new images

        // collect all the images for media overlay:
        this.mainStore.dataStore.mixedMedia.sortedMixedMediaForBricks.forEach(addImg => {
            this.mediaOverlayImages.push(
                { mainImgIndex: mainImgIndex, imgPath: addImg.imageFullPath }); // main images

            if (addImg.additionalInfoImagesFullPaths) { // additional images
                addImg.additionalInfoImagesFullPaths.forEach((image) => {
                    this.mediaOverlayImages.push({ mainImgIndex: mainImgIndex, imgPath: image });
                });
            }

            if (addImg.mainActionImageFullPath) {
                this.mediaOverlayImages.push({ mainImgIndex: mainImgIndex, imgPath: addImg.mainActionImageFullPath });
            }

            if (addImg.secondaryImageFullPath) {
                this.mediaOverlayImages.push({ mainImgIndex: mainImgIndex, imgPath: addImg.secondaryImageFullPath });
            }

            mainImgIndex++;
        });
    }

    updateMediaOverlayVisibility(value, mainImgIndex = 1) {
        this.mediaOverlayImageIndex =
            this.mediaOverlayImages.findIndex(it => it.mainImgIndex === mainImgIndex);

        this.displayMediaOverlay = value;
    }
    setDetailsModalOpen(value: boolean, masterArticleNo?: number, brickNo?: number) {
        if (masterArticleNo) {
            this.masterArticleNo = masterArticleNo;
        }
        if (brickNo) {
            this.brickNo = brickNo;
        }
        this.detailsModalOpen = value;
    }
    getArticleSetItem(): IArticleSetItem | undefined {
        const articleSetItems = this.mainStore.dataStore
            .shutterModule.productInfo.description.sortedArticleSetItemDescriptions;
        const selectedArticleSetItem = articleSetItems.find((item) => {
            return (item.masterArticleNo === this.masterArticleNo) 
                && (item.brickNo === this.brickNo);
        })
        return selectedArticleSetItem;
    }

    replaceImageScales(bricks: IMixedMediaOfBrick[], scale: string,
        additionalImgScale: string, srcScale: string = 'Original') {
        bricks.forEach((item) => {
            item.imageFullPath = item.imageFullPath.replace(srcScale, scale);
            if (scale !== additionalImgScale) {
                if (item.mainActionImageFullPath) {
                    item.mainActionImageFullPath = item.mainActionImageFullPath.replace(srcScale, additionalImgScale);
                }
                if (item.shoeSoleImageFullPath) {
                    item.shoeSoleImageFullPath = item.shoeSoleImageFullPath.replace(srcScale, additionalImgScale);
                }
                if (item.secondaryImageFullPath) {
                    item.secondaryImageFullPath = item.secondaryImageFullPath.replace(srcScale, additionalImgScale);
                }
                item.additionalInfoImagesFullPaths = item.additionalInfoImagesFullPaths?.map((image) => {
                    return image.replace(srcScale, additionalImgScale);
                });
            }

        })
    };

    get getMediaOverlayImageIndex(): number {
        return this.mediaOverlayImageIndex;
    }

    get getMixedMediaImages(): IMixedMediaOfBrick[] {
        const hasSetImage = this.mainStore.dataStore.mixedMedia.flexibleSetImageFullPath ? 1 : 0;
        const mmImages: IMixedMediaOfBrick[]
            = JSON.parse(JSON.stringify(this.mainStore.dataStore.mixedMedia.sortedMixedMediaForBricks));
        const elementCount = mmImages.length + (hasSetImage ? 1 : 0);
        if (elementCount < 3) {
            this.replaceImageScales(mmImages, 'FullHeight', 'FullHeight');
        }
        else if (elementCount === 3) {
            this.replaceImageScales(mmImages, 'HalfTile', 'FullHeight');
            if (!hasSetImage) {
                this.replaceImageScales([mmImages[0]], 'FullHeight', 'FullHeight', 'HalfTile');
            }
        }
        else if (elementCount === 4) {
            this.replaceImageScales(mmImages, 'HalfTile', 'FullHeight');
        }
        else if (elementCount === 5) {
            this.replaceImageScales(mmImages, 'ThirdTile', 'FullHeight');
            if (!hasSetImage) {
                this.replaceImageScales([mmImages[0], mmImages[1]], 'HalfTile', 'FullHeight', 'ThirdTile');
            }
        }
        if (elementCount === 6) {
            this.replaceImageScales(mmImages, 'ThirdTile', 'FullHeight');
        }
        if (elementCount === 7) {
            this.replaceImageScales(mmImages, 'SmallTile', 'HalfTile');
            if (!hasSetImage) {
                this.replaceImageScales([mmImages[0], mmImages[1], mmImages[2]], 'ThirdTile', 'FullHeight', 'SmallTile');
            }
        }
        if (elementCount > 7) {
            this.replaceImageScales(mmImages, 'SmallTile', 'HalfTile');
        }
        return mmImages;
    }

    get getSetImage() {
        const setImage = this.mainStore.dataStore.mixedMedia.flexibleSetImageFullPath;
        const mmImages = this.mainStore.dataStore.mixedMedia.sortedMixedMediaForBricks;
        const elementCount = mmImages.length + (setImage ? 1 : 0);
        if (!setImage) {
            return null;
        }
        if (elementCount < 4) {
            return setImage.replace('Original', 'FullHeight');
        }
        if (elementCount === 4 || elementCount === 5) {
            return setImage.replace('Original', 'HalfTile');
        }
        if (elementCount === 6 || elementCount === 7) {
            return setImage.replace('Original', 'ThirdTile');
        }
        if (elementCount > 7) {
            return setImage.replace('Original', 'SmallTile');
        }
        return setImage;
    }

    get getImageCount(): number {
        const mmImages = this.mainStore.dataStore.mixedMedia.sortedMixedMediaForBricks;
        const setImage = this.mainStore.dataStore.mixedMedia.flexibleSetImageFullPath;
        const count = mmImages.length + (setImage ? 1 : 0);
        return count;
    }

    getAltValue(index = 0): string {
        if (index > 0) return decodeHTML(this.mixedMediaBricks[index].altTagForImages) + ' ' + index;

        return decodeHTML(this.mixedMediaBricks[index].altTagForImages) ?? '';
    }

    get getImagesForCarousel(): (isSmall: boolean) => IMixedMediaForCarousel[] {
        const mmImages = JSON.parse(JSON.stringify(this.mainStore.dataStore.mixedMedia.sortedMixedMediaForBricks));
        return (isSmall: boolean) => {
            const gridSize = isSmall ? 6 : 9;
            const carouselImages: IMixedMediaImage[] = [];
            if (this.mainStore.dataStore.mixedMedia.flexibleSetImageFullPath) {
                carouselImages.push({
                    fullPath: this.mainStore.dataStore.mixedMedia.flexibleSetImageFullPath,
                    isFreeInSet: false, includedQuantity: 0, alt: this.altTagForFlexibleSetImage
                });
            }
            mmImages.forEach((item, index) => {
                carouselImages.push({
                    fullPath: item.imageFullPath, isFreeInSet: item.isFreeInSet,
                    includedQuantity: item.includedQuantity, alt: this.getAltValue(index)
                });
            });
            const finalImages: IMixedMediaForCarousel[] = [];
            for (let i = 0; i < carouselImages.length; i += gridSize) {
                const slideImages = carouselImages.slice(i, i + gridSize);
                finalImages.push({ index: finalImages.length, images: slideImages });
            }

            if (isSmall) {
                finalImages.forEach((item) => {
                    item.images.forEach((image) =>
                        image.fullPath = image.fullPath.replace('Original', 'SmallTile'));
                });
            }
            else {
                finalImages.forEach((item) => {
                    if (item.images.length === 1) {
                        item.images[0].fullPath = item.images[0].fullPath.replace('Original', 'FullHeight');
                    }
                    else if (item.images.length < 6) {
                        item.images.forEach((image, index) => {
                            item.images[index].fullPath = image.fullPath.replace('Original', 'HalfTile');
                        });
                    }
                    else {
                        item.images.forEach((image, index) => {
                            item.images[index].fullPath = image.fullPath.replace('Original', 'SmallTile');
                        });
                    }
                })
            }

            return finalImages;
        }
    }
}

interface IMixedMediaForCarousel {
    index: number;
    images: IMixedMediaImage[];
}

interface IMixedMediaImage {
    fullPath: string;
    isFreeInSet: boolean;
    includedQuantity: number;
    alt?: string;
}
